import { getDisplayName } from 'volta/models/user';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import UserSelect from 'volta/components/user-select';
import BvBtn from 'volta/components/bv-btn';
import { t } from 'ember-intl';
import perform from 'ember-concurrency/helpers/perform';

import type { IActivePlanner } from 'volta/models/types/planning-order';
import type PlanningOrderApi from 'volta/services/planning-order-api';
import type { BvSelectMultiArgs } from 'volta/components/bv-select-multi';
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';

type TFilterUser = IActivePlanner & { displayName?: string };

interface PowFilterUserFilterSignature {
  Args: IFilterArgs<BvSelectMultiArgs>;
  Blocks: {
    default: [any, Dropdown];
  };
  Element: HTMLElement;
}

export default class PoWorkbenchFiltersUserFilterComponent extends Component<PowFilterUserFilterSignature> {
  /**
   * Services
   */

  @service planningOrderApi!: PlanningOrderApi;

  /**
   * Getters
   */
  get users(): TFilterUser[] {
    return this.planningOrderApi.users.map((user: TFilterUser) => {
      user.displayName = getDisplayName(user);
      return user;
    });
  }

  get selectedUser() {
    const { values = [] } = this.args;
    return values.length > 0 ? this.users.find((u) => u.userId === values[0]) : undefined;
  }

  get selectedUsers() {
    const { values = [] } = this.args;
    return this.users.filter((u) => values.includes(u.userId));
  }

  /**
   * Lifecycle events
   */

  constructor(owner: any, args: PowFilterUserFilterSignature['Args']) {
    super(owner, args);

    if (this.users.length === 0) {
      this.planningOrderApi.usersTask.perform();
    }
  }

  /**
   * Actions
   */
  @action
  handleChangeUser(value?: TFilterUser) {
    this.args.onChange?.(value ? [value.userId] : []);
  }

  @action
  handleChangeManyUsers(values: TFilterUser[]) {
    this.args.onChange?.(values.map((v) => v.userId));
  }

  <template>
    <UserSelect
      @multiple={{@componentArgs.multiple}}
      @triggerClass='po-workbench__user-select'
      @options={{this.users}}
      @disabled={{false}}
      @selected={{if @componentArgs.multiple this.selectedUsers this.selectedUser}}
      @placeholder={{t 'poWorkbench.selectPOUser'}}
      @isLoading={{this.planningOrderApi.usersTask.isRunning}}
      @onChange={{if @componentArgs.multiple this.handleChangeManyUsers this.handleChangeUser}}
      @fill={{true}}
      @isRequired={{@required}}
      @matchTriggerWidth={{true}}
      @afterOptionsComponent={{component
        BvBtn
        text=(t 'reload')
        isLoading=this.planningOrderApi.usersTask.isRunning
        subtle=true
        primary=true
        onClick=(perform this.planningOrderApi.usersTask)
        class='u-m-1 u-flex-fill'
      }}
    />
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'PoWorkbench::Filters::UserFilter': typeof PoWorkbenchFiltersUserFilterComponent;
    'po-workbench/filters/user-filter': typeof PoWorkbenchFiltersUserFilterComponent;
  }
}
